<template>
  <div class="sidebar">
    <div class="main-sidebar" :class="{ open: isMainNavOpen }">
      <div class="main-header w-100">
        <HMLogo class="logo" />
        <div class="close-icon">
          <font-awesome-icon :icon="['far', 'times']" @click="closeMainNav" />
        </div>
      </div>
      <div
        class="d-flex flex-column align-items-start flex-grow-1 w-100"
        :class="{
          'justify-content-between': hasPermission('has_main_menu'),
          'justify-content-start': !hasPermission('has_main_menu'),
        }"
      >
        <div
          class="main-nav"
          :class="{ 'px-4 py-1': !hasPermission('has_main_menu') }"
        >
          <div
            v-for="({ collapse, navItems }, navGroupIndex) in navGroups"
            :key="navGroupIndex"
          >
            <NavCollapse
              v-if="collapse"
              :text="collapse.text || $t(collapse.textKey)"
              :icon="collapse.locked ? ['far', 'lock-alt'] : collapse.icon"
              :isOpen="collapse.isOpen"
              @click.native="collapseClick(collapse.key)"
            />
            <div
              :class="{ 'collapse-menu': collapse }"
              v-show="!collapse || collapse.isOpen"
            >
              <router-link
                :to="navItem.to"
                v-for="(navItem, navItemIndex) in navItems"
                :key="navItemIndex"
                class="undecorated-router-link"
                :target="navItem.target"
                :custom="navItem.custom"
              >
                <a
                  v-if="!navItem.hidden && navItem.custom"
                  :href="navItem.to"
                  class="undecorated-router-link"
                  :target="navItem.target"
                >
                  <SidebarNavItem
                    :class="navItem.cssClass"
                    @click="closeMenu"
                    :text="$t(navItem.textKey)"
                    :icon="navItem.locked ? ['far', 'lock-alt'] : navItem.icon"
                    :active="navItem.active"
                    :type="navItem.type"
                    :isBeta="navItem.isBeta"
                  />
                </a>
                <SidebarNavItem
                  v-if="!navItem.hidden && !navItem.custom"
                  :class="navItem.cssClass"
                  @click="closeMenu"
                  :text="$t(navItem.textKey)"
                  :icon="navItem.locked ? ['far', 'lock-alt'] : navItem.icon"
                  :active="navItem.active"
                  :type="navItem.type"
                  :isBeta="navItem.isBeta"
                />
              </router-link>
            </div>
          </div>
        </div>
        <div
          class="main-midsection"
          :class="{ 'px-4 py-1': !hasPermission('has_main_menu') }"
        >
          <router-link
            v-if="hasPermission('has_bio')"
            to="/bio"
            class="undecorated-router-link"
          >
            <SidebarNavItem :active="false">
              <img
                src="/assets/images/marvelous-bio-sidebar.svg"
                width="150px"
                class="mr-2"
                :class="{ 'ml-1': hasPermission('has_main_menu') }"
              />
            </SidebarNavItem>
          </router-link>
        </div>
        <div
          class="main-footer"
          :class="{ 'px-4 py-1': !hasPermission('has_main_menu') }"
        >
          <router-link to="/share-and-earn" class="undecorated-router-link">
            <SidebarNavItem
              :text="$t('GENERAL.LABEL.SHARE_AND_EARN')"
              :active="false"
              @click="closeMenu"
            >
              <img
                src="/assets/images/MoneyHeart.svg"
                width="20px"
                class="mr-2"
                :class="{ 'ml-1': hasPermission('has_main_menu') }"
              />
            </SidebarNavItem>
          </router-link>
          <a
            :href="config.helpURL"
            target="_blank"
            class="undecorated-router-link"
          >
            <SidebarNavItem
              :text="$t('GENERAL.LINK.HELP_CENTER')"
              :icon="['far', 'hands-heart']"
              @click="closeMenu"
            />
          </a>
          <router-link
            to="/settings"
            class="undecorated-router-link"
            v-if="hasPermission('has_settings_menu')"
          >
            <SidebarNavItem
              :text="$t('GENERAL.LINK.SETTINGS')"
              :icon="['far', 'wrench']"
              @click="closeMenu"
            />
          </router-link>
        </div>
      </div>
    </div>
    <div class="bar-sidebar">
      <HMIconLogo class="m-2" height="18px" />
      <div class="bar-nav">
        <div
          v-for="({ collapse, navItems }, navGroupIndex) in navGroups"
          :key="navGroupIndex"
        >
          <SidebarBarItem
            :id="collapse.key"
            :icon="collapse.icon"
            :active="collapse.active"
            v-if="collapse"
          />
          <HMPopover :target="collapse.key" v-if="collapse">
            <router-link
              :to="navItem.to"
              v-for="(navItem, navItemIndex) in navItems"
              :key="navItemIndex"
              class="undecorated-router-link"
            >
              <HMPopoverMenuItem
                :class="{ 'text-primary': navItem.active }"
                @click="closeMenu"
              >
                {{ $t(navItem.textKey) }}
              </HMPopoverMenuItem>
            </router-link>
          </HMPopover>
          <div v-else>
            <router-link
              :to="navItem.to"
              v-for="(navItem, navItemIndex) in navItems"
              :key="navItemIndex"
              class="undecorated-router-link"
            >
              <SidebarBarItem
                :icon="navItem.icon"
                :active="navItem.active"
                @click="closeMenu"
              />
            </router-link>
          </div>
        </div>
        <div></div>
      </div>
      <div class="midsection py-3">
        <router-link
          v-if="hasPermission('has_bio')"
          to="/bio"
          class="undecorated-router-link"
        >
          <SidebarNavItem :active="false">
            <img
              src="/assets/images/bio-icon.png"
              width="30px"
              :class="{ 'ml-1': hasPermission('has_main_menu') }"
            />
          </SidebarNavItem>
        </router-link>
      </div>
      <div>
        <router-link to="/share-and-earn" class="undecorated-router-link">
          <SidebarNavItem :active="false">
            <img
              src="/assets/images/MoneyHeart.svg"
              width="20px"
              :class="{ 'ml-1': hasPermission('has_main_menu') }"
            />
          </SidebarNavItem>
        </router-link>
        <a
          :href="config.helpURL"
          target="_blank"
          class="undecorated-router-link"
        >
          <SidebarBarItem :icon="['far', 'hands-heart']" />
        </a>
        <router-link
          to="/settings"
          class="undecorated-router-link"
          v-if="hasPermission('has_settings_menu')"
        >
          <SidebarBarItem :icon="['far', 'wrench']" @click="closeMenu" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import SidebarNavItem from './SidebarNavItem.vue';
import SidebarBarItem from './SidebarBarItem.vue';
import NavCollapse from './NavCollapse.vue';
import { mapGetters, mapState } from 'vuex';
import { getConfig } from '@/web/utils/configuration';
import TeacherPermissionsMixin from '@/web/mixins/TeacherPermissionsMixin';
import { getStudentViewURL } from '@/web/router/custom-domain-utils';

export default Vue.extend({
  components: {
    SidebarNavItem,
    SidebarBarItem,
    NavCollapse,
  },
  mixins: [TeacherPermissionsMixin],
  data() {
    return {
      navGroups: [],
      settingGroup: {
        collapse: {
          text: 'Settings',
          icon: ['far', 'wrench'],
          key: 'settings',
          isOpen: false,
        },
        navItems: [
          {
            icon: ['far', 'long-arrow-alt-right'],
            to: '/settings/account',
            text: 'Account',
            active: false,
          },
        ],
      },
    };
  },
  computed: {
    ...mapState('globals', ['isMainNavOpen']),
    ...mapGetters({
      teacher: 'user/teacher',
      teacherMagicCode: 'user/teacherMagicCode',
      userFullName: 'user/userFullName',
    }),
    config() {
      return getConfig();
    },
  },
  methods: {
    markRouteActive(path) {
      this.navGroups.forEach(({ collapse, navItems }) => {
        if (collapse) {
          // Collapse not active collapses
          // collapse.isOpen = false;
          collapse.active = false;
        }
        navItems.forEach(item => {
          item.active = item.to === path;
          if (collapse && item.active) {
            collapse.isOpen = true;
            collapse.active = true;
          }
        });
      });
    },
    closeMainNav() {
      this.$store.dispatch('globals/openMainNav', false);
    },
    collapseClick(collapseKey) {
      this.navGroups.forEach(group => {
        const { collapse } = group;
        if (collapse && collapse.key === collapseKey) {
          collapse.isOpen = !collapse.isOpen;
          if (collapse.defaultRoute && !collapse.defaultRouteLocked) {
            this.$router.push(collapse.defaultRoute);
          }
        }
      });
    },
    teacherName() {
      if (this.userFullName) {
        return this.userFullName;
      } else if (this.teacher.first_name && this.teacher.last_name) {
        return this.teacher.first_name + ' ' + this.teacher.last_name;
      } else return this.teacher.studio_name;
    },
    closeMenu() {
      this.$store.dispatch('globals/openMainNav', false);
    },
    getStudentViewURL(studioUrl) {
      return getStudentViewURL(studioUrl, '/', this.teacherMagicCode);
    },
    setupMenu() {
      this.navGroups = [
        {
          collapse: {
            text: this.teacherName(),
            icon: ['far', 'alicorn'],
            key: 'user',
            isOpen: false,
            active: false,
          },
          navItems: [
            {
              icon: ['far', 'long-arrow-alt-right'],
              to: this.getStudentViewURL(this.teacher?.studio_url),
              custom: true,
              textKey: 'GENERAL.LINK.VIEW_AS_STUDENT',
              active: false,
              target: '_blank',
              hidden: !this.hasPermission('has_view_as_student_menu'),
            },
            {
              icon: ['far', 'long-arrow-alt-right'],
              to: '/settings/adjust-plan',
              textKey: 'GENERAL.LINK.UPGRADE',
              active: false,
              hidden: !this.hasPermission('has_upgrade_menu'),
            },
            {
              icon: ['far', 'long-arrow-alt-right'],
              to: '/logout',
              textKey: 'GENERAL.LINK.LOGOUT',
              active: false,
            },
          ],
        },
      ];
      if (this.hasPermission('has_main_menu')) {
        this.navGroups = [
          ...this.navGroups,
          ...[
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'home-heart'],
                  to: '/dashboard',
                  textKey: 'DASHBOARD.LABEL.DASHBOARD',
                  active: false,
                },
              ],
            },
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'calendar'],
                  to: '/events/calendar',
                  textKey: 'GENERAL.LABEL.CALENDAR',
                  active: false,
                },
              ],
            },
            {
              collapse: {
                textKey: 'GENERAL.LABEL.MEDIA_LIBRARY',
                icon: ['far', 'folder-open'],
                key: 'media-library',
                isOpen: false,
                active: false,
                defaultRoute: '/media/library',
                defaultRouteLocked: !this.hasPermission('has_media_library'),
              },
              navItems: [
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/media/library',
                  textKey: 'GENERAL.LINK.LIBRARY',
                  active: false,
                  locked: !this.hasPermission('has_media_library'),
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/media/categories',
                  textKey: 'GENERAL.LINK.CATEGORIES',
                  active: false,
                },
              ],
            },
            {
              collapse: {
                textKey: 'GENERAL.LABEL.MARKETING',
                icon: ['far', 'share-alt'],
                key: 'marketing',
                isOpen: false,
                active: false,
                defaultRoute: '/marketing/emails',
              },
              navItems: [
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/marketing/emails',
                  textKey: 'GENERAL.LINK.EMAILS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/marketing/meeting-types',
                  textKey: 'GENERAL.LINK.MEETINGS',
                  active: false,
                  isBeta: true,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/marketing/marketing-assets',
                  textKey: 'GENERAL.LINK.MARKETING_ASSETS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/marketing/announcements',
                  textKey: 'GENERAL.LINK.ANNOUNCEMENT',
                  active: false,
                },
              ],
            },
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'users'],
                  to: '/customers',
                  textKey: 'GENERAL.LABEL.CUSTOMERS',
                  active: false,
                },
              ],
            },
            {
              collapse: {
                textKey: 'GENERAL.LABEL.ORDERS',
                icon: ['far', 'shopping-bag'],
                key: 'Orders',
                isOpen: false,
                active: false,
                defaultRoute: '/orders',
              },
              navItems: [
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/orders',
                  textKey: 'GENERAL.LINK.OVERVIEW',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/payment-plans',
                  textKey: 'GENERAL.LINK.PAYMENT_PLANS',
                  active: false,
                  locked: !this.hasPermission('has_payment_plans'),
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/gift-purchases',
                  textKey: 'GENERAL.LINK.GIFT_PURCHASES',
                  active: false,
                  locked: !this.hasPermission('has_payment_plans'),
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/tip-jar',
                  textKey: 'GENERAL.LINK.TIP_JAR',
                  active: false,
                },
              ],
            },
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'comments'],
                  to: '/community',
                  textKey: 'GENERAL.LABEL.COMMUNITY',
                  active: false,
                },
              ],
            },
            {
              collapse: {
                textKey: 'GENERAL.LABEL.PRODUCTS',
                icon: ['far', 'box-open'],
                key: 'products',
                isOpen: false,
                active: false,
                defaultRoute: '/products/my-products',
              },
              navItems: [
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/my-products',
                  textKey: 'GENERAL.LINK.MY_PRODUCTS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/product-tags',
                  textKey: 'GENERAL.LINK.PRODUCT_TAGS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/coupons',
                  textKey: 'GENERAL.LINK.COUPONS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/contracts',
                  textKey: 'GENERAL.LINK.CONTRACTS',
                  active: false,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/podcasts',
                  textKey: 'GENERAL.LINK.PODCASTS',
                  active: false,
                  isBeta: true,
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/tax-rates',
                  textKey: 'GENERAL.LINK.TAX_RATES',
                  active: false,
                  locked: !this.hasPermission('has_taxes'),
                },
                {
                  icon: ['far', 'long-arrow-alt-right'],
                  to: '/products/quizzes',
                  textKey: 'GENERAL.LABEL.QUIZZES',
                  active: false,
                  locked: !this.hasPermission('has_quizzes'),
                },
              ],
            },
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'chart-bar'],
                  to: '/reports',
                  textKey: 'GENERAL.LABEL.REPORTS',
                  active: false,
                },
              ],
            },
            {
              collapse: null,
              navItems: [
                {
                  icon: ['far', 'file-invoice'],
                  to: '/pages',
                  textKey: 'GENERAL.LABEL.PAGES',
                  active: false,
                  isBeta: true,
                  locked: !(
                    this.hasPermission('has_page_builder') ||
                    this.teacher?.is_page_builder_enabled
                  ),
                },
              ],
            },
          ],
        ];
      }
    },
  },
  watch: {
    $route: {
      handler(to) {
        this.markRouteActive(to.path);
      },
      immediate: true,
    },
    teacher: {
      handler(teacher) {
        if (teacher) {
          this.setupMenu();
        }
        this.markRouteActive(this.$route.path);
      },
      immediate: true,
    },
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.sidebar {
  z-index: 10;
}

.main-header {
  border-bottom: 1px solid $medium-gray;
  padding: 1rem 0.5rem 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  @include media-breakpoint-up(md) {
    border-bottom: none;
    padding: 2rem 2rem 2.5rem;
  }
}

.close-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 3rem;
  height: 30px;
  cursor: pointer;
  @include media-breakpoint-up(md) {
    display: none;
  }
}

.main-sidebar {
  flex-direction: column;

  display: none;
  height: 100%;
  overflow: hidden;
  overflow-y: auto;

  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  background-color: white;

  align-items: flex-start;
  justify-content: space-between;

  &::-webkit-scrollbar {
    width: 6px;
    background-color: $light-gray;
  }

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.1);
    border-radius: 10px;
    background-color: white;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
    background-color: $light-gray;
  }

  @include media-breakpoint-up(lg) {
    display: flex;
    position: static;
    width: 300px;
  }
  &.open {
    @include media-breakpoint-down(sm) {
      display: flex;
    }
  }
}

.logo {
  max-width: 180px;
  height: 30px;
  align-self: flex-start;
  @include media-breakpoint-up(md) {
    max-width: 100%;
  }
}

.main-nav {
  width: 100%;

  padding: 2rem 1rem 0;
  @include media-breakpoint-up(md) {
    padding: 0 1.5rem;
  }
}

.link {
  display: block;
  text-decoration: none;
  padding: 0.5rem 0;
}

.collapse-menu {
  margin-left: 1.5rem;
}

.primary ::v-deep .text {
  color: $coral !important;
}

// bar-sidebar
.bar-sidebar {
  padding: 4px;
  border-right: 1px solid $medium-gray;
  flex-direction: column;

  display: none;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  @include media-breakpoint-up(md) {
    display: flex;
  }
  @include media-breakpoint-up(lg) {
    display: none;
  }

  .midsection {
    flex: 0.5;
  }
}

.bar-nav {
  flex: 1;
}

.bar-popover {
  padding: 0;
}

.main-midsection {
  padding: 2rem 1rem 1rem;
  @include media-breakpoint-up(md) {
    padding: 2rem 1.5rem 1rem;
  }
}

.main-footer {
  padding: 1rem 1rem 1rem;
  @include media-breakpoint-up(md) {
    padding: 1rem 1.5rem 1rem;
  }
}
</style>
